import type { IDBPDatabase, IndexNames, StoreNames, StoreValue } from 'idb'
import { openDatabase, type MyMDVDB, type ServicesDB } from '@/db/schema'
import { nowISO } from '@/lib/utils'

type StoreName = StoreNames<MyMDVDB>
type Value<S extends StoreName> = StoreValue<MyMDVDB, S>
type Index<S extends StoreName> = IndexNames<MyMDVDB, S>

/**
 * idb's generics resolve against a *concrete* store name; they cannot be
 * satisfied by a still-generic `S`. Every method below is fully typed at its
 * public boundary, and reaches for this untyped handle internally rather than
 * scattering casts through the call sites.
 */
function anyStore(db: ServicesDB): IDBPDatabase {
  return db as unknown as IDBPDatabase
}

/**
 * Thin, typed CRUD facade over IndexedDB.
 *
 * The mock API layer is the only consumer — components never touch the
 * database directly, which is what makes swapping in a real Express backend a
 * one-file change.
 */
export const repo = {
  async db(): Promise<ServicesDB> {
    return openDatabase()
  },

  async all<S extends StoreName>(store: S): Promise<Value<S>[]> {
    const db = await openDatabase()
    return (await anyStore(db).getAll(store)) as Value<S>[]
  },

  async get<S extends StoreName>(store: S, id: string): Promise<Value<S> | undefined> {
    const db = await openDatabase()
    return (await anyStore(db).get(store, id)) as Value<S> | undefined
  },

  async byIndex<S extends StoreName>(
    store: S,
    index: Index<S>,
    key: IDBValidKey,
  ): Promise<Value<S>[]> {
    const db = await openDatabase()
    return (await anyStore(db).getAllFromIndex(store, index as string, key)) as Value<S>[]
  },

  async put<S extends StoreName>(store: S, value: Value<S>): Promise<Value<S>> {
    const db = await openDatabase()
    await anyStore(db).put(store, value)
    return value
  },

  async putMany<S extends StoreName>(store: S, values: Value<S>[]): Promise<void> {
    if (values.length === 0) return
    const db = await openDatabase()
    const tx = anyStore(db).transaction(store, 'readwrite')
    await Promise.all([...values.map((value) => tx.store.put(value)), tx.done])
  },

  /** Shallow-merges `patch` into the stored record and refreshes `updatedAt`. */
  async patch<S extends StoreName>(
    store: S,
    id: string,
    patch: Partial<Value<S>>,
  ): Promise<Value<S> | undefined> {
    const db = await openDatabase()
    const existing = (await anyStore(db).get(store, id)) as Value<S> | undefined
    if (!existing) return undefined
    const next = { ...existing, ...patch, updatedAt: nowISO() } as Value<S>
    await anyStore(db).put(store, next)
    return next
  },

  async remove(store: StoreName, id: string): Promise<void> {
    const db = await openDatabase()
    await anyStore(db).delete(store, id)
  },

  /** Cascade helper: deletes every record in `store` whose `index` equals `key`. */
  async removeByIndex<S extends StoreName>(
    store: S,
    index: Index<S>,
    key: IDBValidKey,
  ): Promise<number> {
    const db = await openDatabase()
    const matches = (await anyStore(db).getAllFromIndex(
      store,
      index as string,
      key,
    )) as Array<{ id: string }>
    const tx = anyStore(db).transaction(store, 'readwrite')
    await Promise.all([...matches.map((match) => tx.store.delete(match.id)), tx.done])
    return matches.length
  },

  async count(store: StoreName): Promise<number> {
    const db = await openDatabase()
    return anyStore(db).count(store)
  },

  async clearAll(): Promise<void> {
    const db = await openDatabase()
    const stores = [...db.objectStoreNames]
    const tx = anyStore(db).transaction(stores, 'readwrite')
    await Promise.all([...stores.map((store) => tx.objectStore(store).clear()), tx.done])
  },
}
